import * as v from 'valibot';
import { getOffsetNumber } from './utils.ts';

// Common Track Types

export const SortSchema = v.union([
  v.literal('name'),
  v.literal('-name'),
  v.literal('dateAdded'),
  v.literal('-dateAdded'),
]);

export const ArtistViewSchema = v.union([
  v.literal('latest-release'),
  v.literal('latestRelease'),
  v.literal('top-songs'),
  v.literal('topSongs'),
  v.literal('full-albums'),
  v.literal('fullAlbums'),
  v.literal('featured-albums'),
  v.literal('featuredAlbums'),
  v.literal('featured-playlists'),
  v.literal('featuredPlaylists'),
  v.literal('singles'),
  v.literal('live-albums'),
  v.literal('liveAlbums'),
  v.literal('compilation-albums'),
  v.literal('compilationAlbums'),
  v.literal('appears-on-albums'),
  v.literal('appearsOnAlbums'),
  v.literal('similar-artists'),
  v.literal('similarArtists'),
]);

export type SortType = v.InferOutput<typeof SortSchema>;

export const ArtWorkAttrSchema = v.pipe(
  v.object({
    url: v.string(),
    bgColor: v.optional(v.string()),
    textColor1: v.optional(v.string()),
    textColor2: v.optional(v.string()),
    textColor3: v.optional(v.string()),
    textColor4: v.optional(v.string()),
  }),
  v.transform(
    ({ url, bgColor, textColor1, textColor2, textColor3, textColor4 }) => {
      const result: {
        url: string;
        bgColor?: string;
        textColors?: string[];
      } = {
        url,
      };

      if (bgColor !== undefined) {
        result.bgColor = bgColor;
      }

      const textColors = [textColor1, textColor2, textColor3, textColor4]
        .filter(
          (c): c is string => !!c,
        );

      if (textColors.length > 0) {
        result.textColors = textColors;
      }

      return result;
    },
  ),
);

export type ArtWorkAttrType = v.InferOutput<typeof ArtWorkAttrSchema>;

// -------------------------
// Music Videos
// -------------------------

export const MusicVideoTrackItemSchema = v.pipe(
  v.object({
    id: v.string(),
    type: v.union([
      v.literal('music-videos'),
      v.literal('library-music-videos'),
    ]),
    attributes: v.pipe(
      v.object({
        artistName: v.optional(v.string()),
        artwork: v.optional(ArtWorkAttrSchema),
        durationInMillis: v.number(),
        name: v.string(),
        playParams: v.object({
          id: v.string(),
          kind: v.literal('musicVideo'),
          isLibrary: v.optional(v.boolean()),
        }),
        releaseDate: v.optional(v.string()),
        trackNumber: v.optional(v.number()),
        contentRating: v.optional(v.string()),
      }),
      v.transform((
        {
          artistName,
          artwork,
          name,
          playParams,
          releaseDate,
          trackNumber,
          contentRating,
        },
      ) => ({
        artistName,
        artwork,
        name,
        isLibrary: playParams.isLibrary ?? false,
        releaseDate,
        trackNumber,
        contentRating,
      })),
    ),
  }),
  v.transform(({ id, attributes }) => {
    const {
      artistName,
      artwork,
      name,
      isLibrary,
      releaseDate,
      trackNumber,
      contentRating,
    } = attributes;
    return {
      id,
      type: 'music-video' as const,
      artistName,
      artwork,
      name,
      isLibrary,
      releaseDate,
      trackNumber,
      contentRating,
    };
  }),
);

export type MusicVideoTrackItem = v.InferOutput<
  typeof MusicVideoTrackItemSchema
>;

// -------------------------
// Playlist
// -------------------------

// https://developer.apple.com/documentation/applemusicapi/playlists

export const PlaylistTrackItemSchema = v.pipe(
  v.object({
    id: v.string(),
    type: v.union([v.literal('songs'), v.literal('library-songs')]),
    attributes: v.pipe(
      v.object({
        artwork: v.optional(ArtWorkAttrSchema),
        artistName: v.string(),
        albumName: v.string(),
        discNumber: v.number(),
        name: v.string(),
        playParams: v.object({
          id: v.string(),
          kind: v.literal('song'),
          isLibrary: v.optional(v.boolean()),
        }),
        trackNumber: v.number(),
        contentRating: v.optional(v.string()),
      }),
      v.transform(
        (
          {
            artwork,
            artistName,
            albumName,
            contentRating,
            discNumber,
            name,
            playParams,
            trackNumber,
          },
        ) => ({
          artwork,
          artistName,
          albumName,
          discNumber,
          isLibrary: playParams.isLibrary ?? false,
          name,
          trackNumber,
          contentRating,
        }),
      ),
    ),
  }),
  v.transform(({ id, attributes }) => {
    const {
      artwork,
      artistName,
      albumName,
      contentRating,
      discNumber,
      isLibrary,
      name,
      trackNumber,
    } = attributes;
    return {
      id,
      artwork,
      artistName,
      albumName,
      discNumber,
      isLibrary,
      name,
      trackNumber,
      contentRating,
      type: 'track' as const,
    };
  }),
);
export type PlaylistTrackItem = v.InferOutput<
  typeof PlaylistTrackItemSchema
>;

export const PlaylistItemSchema = v.pipe(
  v.object({
    id: v.string(),
    type: v.union([v.literal('playlists'), v.literal('library-playlists')]),
    attributes: v.optional(
      v.object({
        artwork: v.optional(ArtWorkAttrSchema),
        name: v.string(),
        canEdit: v.optional(v.boolean()),
        playParams: v.object({
          id: v.string(),
          kind: v.literal('playlist'),
          isLibrary: v.optional(v.boolean()),
        }),
      }),
    ),
    relationships: v.optional(
      v.object({
        tracks: v.object({
          data: v.array(
            v.union([PlaylistTrackItemSchema, MusicVideoTrackItemSchema]),
          ),
        }),
      }),
    ),
  }),
  v.transform(({ type, id, attributes, relationships }) => {
    const artwork = attributes?.artwork;
    const name = attributes?.name ?? '';
    const canEdit = attributes?.canEdit ?? false;

    return {
      id,
      type: 'playlist' as const,
      canEdit: canEdit ?? false,
      name,
      artwork,
      isLibrary: (() => {
        if (!attributes || !attributes.playParams) {
          return type === 'library-playlists';
        }
        return attributes?.playParams.isLibrary ?? false;
      })(),
      tracks: ((relationships) => {
        if (relationships) {
          return relationships.tracks.data;
        }
        return undefined;
      })(relationships),
    };
  }),
);

export type PlaylistItem = v.InferOutput<typeof PlaylistItemSchema>;

export type PlaylistItemTypeWithTracks = Omit<PlaylistItem, 'tracks'> & {
  tracks: NonNullable<PlaylistItem['tracks']>;
};

// -------------------------
// Album
// -------------------------

export const AlbumTrackItemSchema = v.pipe(
  v.object({
    id: v.string(),
    type: v.union([v.literal('songs'), v.literal('library-songs')]),
    attributes: v.pipe(
      v.object({
        albumName: v.string(),
        artistName: v.string(),
        artwork: v.optional(ArtWorkAttrSchema),
        discNumber: v.number(),
        name: v.string(),
        trackNumber: v.number(),
        contentRating: v.optional(v.string()),
        playParams: v.object({
          id: v.string(),
          kind: v.literal('song'),
          isLibrary: v.optional(v.boolean()),
        }),
      }),
      v.transform(
        ({
          albumName,
          artistName,
          artwork,
          discNumber,
          name,
          trackNumber,
          contentRating,
          playParams,
        }) => ({
          albumName,
          artistName,
          artwork,
          discNumber,
          name,
          trackNumber,
          contentRating,
          isLibrary: playParams.isLibrary ?? false,
        }),
      ),
    ),
  }),
  v.transform(({ id, attributes }) => {
    const {
      albumName,
      artistName,
      artwork,
      discNumber,
      isLibrary,
      name,
      trackNumber,
      contentRating,
    } = attributes;
    return {
      id,
      type: 'track' as const,
      albumName,
      artistName,
      artwork,
      discNumber,
      isLibrary,
      name,
      trackNumber,
      contentRating,
    };
  }),
);

export type AlbumTrackItem = v.InferOutput<typeof AlbumTrackItemSchema>;

// https://developer.apple.com/documentation/applemusicapi/libraryalbums
export const AlbumItemSchema = v.pipe(
  v.object({
    id: v.string(),
    type: v.union([v.literal('library-albums'), v.literal('albums')]),
    attributes: v.object({
      releaseDate: v.optional(v.string()),
      artwork: v.optional(ArtWorkAttrSchema),
      trackCount: v.number(),
      name: v.string(),
      artistName: v.string(),
      playParams: v.optional(v.object({
        id: v.string(),
        kind: v.literal('album'),
        isLibrary: v.optional(v.boolean()),
      })),
    }),
    relationships: v.optional(
      v.object({
        tracks: v.optional(v.object({
          data: v.array(
            v.union([AlbumTrackItemSchema, MusicVideoTrackItemSchema]),
          ),
        })),
        catalog: v.optional(v.object({
          data: v.array(v.pipe(
            v.object({
              id: v.string(), // catalog album id
              type: v.literal('albums'),
              attributes: v.pipe(
                v.object({
                  releaseDate: v.optional(v.string()),
                  artwork: v.optional(ArtWorkAttrSchema),
                  trackCount: v.number(),
                  name: v.string(),
                  artistName: v.string(),
                }),
                v.transform((
                  {
                    releaseDate,
                    artwork,
                    trackCount,
                    name,
                    artistName,
                  },
                ) => ({
                  releaseDate,
                  artwork,
                  trackCount,
                  name,
                  artistName,
                  isLibrary: false,
                })),
              ),
            }),
            v.transform(({ id, attributes }) => ({
              id,
              type: 'album' as const,
              ...attributes,
            })),
          )),
        })),
        artists: v.optional(v.object({
          data: v.array(v.object({
            id: v.string(), // library artist id or catalog artist id
            type: v.union([v.literal('library-artists'), v.literal('artists')]),
            relationships: v.optional(v.object({
              catalog: v.object({
                data: v.array(
                  v.pipe(
                    v.object({
                      id: v.string(), // catalog artist id
                      type: v.literal('artists'),
                      attributes: v.object({
                        name: v.string(),
                        artwork: v.optional(ArtWorkAttrSchema),
                      }),
                    }),
                    v.transform(({ id, attributes }) => ({
                      id,
                      type: 'artist' as const,
                      ...attributes,
                      isLibrary: false,
                    })),
                  ),
                ),
              }),
            })),
          })),
        })),
      }),
    ),
  }),
  v.transform(({ id, type, attributes, relationships }) => {
    const artwork = attributes?.artwork;
    const artistName = attributes?.artistName ?? '';
    const name = attributes?.name ?? '';
    const releaseDate = attributes?.releaseDate ?? undefined;
    const trackCount = attributes?.trackCount ?? 0;
    const playParams = attributes?.playParams;

    return {
      id,
      type: 'album' as const,
      artwork,
      artistName,
      name,
      releaseDate,
      trackCount,
      isLibrary: (() => {
        if (!attributes || !attributes.playParams) {
          return type === 'library-albums';
        }
        return playParams?.isLibrary ?? false;
      })(),
      tracks: (() => {
        if (!relationships || !relationships.tracks) {
          return undefined;
        }
        return relationships.tracks.data;
      })(),
      ...(relationships?.artists || relationships?.catalog
        ? (() => {
          const catalogAlbumData = relationships.catalog?.data[0];
          const libraryArtistsData = relationships.artists?.data[0];
          const catalogArtistsData = libraryArtistsData?.relationships?.catalog
            .data[0];
          return (catalogAlbumData || catalogArtistsData)
            ? {
              catalog: {
                artist: catalogArtistsData && {
                  ...catalogArtistsData,
                  type: 'artist' as const,
                },
                album: catalogAlbumData && {
                  ...catalogAlbumData,
                  type: 'album' as const,
                },
              },
            }
            : {};
        })()
        : {}),
    };
  }),
);

export type AlbumItem = v.InferOutput<typeof AlbumItemSchema>;

// -------------------------
// Station
// -------------------------

// https://developer.apple.com/documentation/applemusicapi/stations
export const StationItemSchema = v.pipe(
  v.object({
    id: v.string(),
    type: v.literal('stations'),
    attributes: v.object({
      artwork: v.optional(ArtWorkAttrSchema),
      name: v.string(),
      isLive: v.boolean(),
      playParams: v.object({
        id: v.string(),
        kind: v.literal('radioStation'),
        format: v.union([
          v.literal('stream'),
          v.literal('tracks'),
        ]),
        hasDrm: v.boolean(),
      }),
    }),
  }),
  v.transform(({ id, attributes }) => {
    const { artwork, name, isLive, playParams } = attributes;
    const { format, hasDrm } = playParams;

    return {
      id,
      type: 'station' as const,
      artwork,
      name,
      isLive,
      format,
      hasDrm,
    };
  }),
);

export type StationItem = v.InferOutput<typeof StationItemSchema>;

// -------------------------
// Artist
// -------------------------

export const ArtistItemSchema = v.pipe(
  v.object({
    id: v.string(),
    type: v.union([v.literal('artists'), v.literal('library-artists')]),
    attributes: v.object({
      name: v.string(),
      artwork: v.optional(ArtWorkAttrSchema),
    }),
    relationships: v.optional(v.object({
      catalog: v.optional(v.object({
        data: v.array(v.object({
          id: v.string(),
          type: v.literal('artists'),
          attributes: v.object({
            name: v.string(),
            artwork: v.optional(ArtWorkAttrSchema),
          }),
        })),
      })),
    })),
  }),
  v.transform(({ id, type, attributes, relationships }) => {
    const attrArtwork = attributes.artwork;
    const catalogData = relationships?.catalog &&
      relationships?.catalog.data[0];
    return {
      id,
      type: 'artist' as const,
      name: attributes.name,
      artwork: (() => {
        if (attrArtwork) {
          return attrArtwork;
        }
        return catalogData ? catalogData.attributes.artwork : undefined;
      })(),
      isLibrary: type === 'library-artists',
      ...(catalogData
        ? (() => {
          return {
            catalogId: catalogData.id, // obsolete, use `catalog` object instead.
            catalog: {
              artist: {
                id: catalogData.id,
                type: 'artist' as const,
                name: catalogData.attributes.name,
                artwork: catalogData.attributes.artwork,
                isLibrary: false,
              },
              album: undefined,
            },
          };
        })()
        : {}),
    };
  }),
);

export type ArtistItem = v.InferOutput<typeof ArtistItemSchema>;

export const ArtistListSchema = v.pipe(
  v.object({
    next: v.optional(v.string()),
    data: v.array(ArtistItemSchema),
    meta: v.object({
      total: v.number(),
    }),
  }),
  v.transform(({ next, data, meta }) => {
    return {
      ...(next ? { next: getOffsetNumber(next) } : {}),
      artists: data,
      total: meta.total,
    };
  }),
);

export type ArtistList = v.InferOutput<typeof ArtistListSchema>;

// -------------------------
// Songs
// -------------------------

export const SongItemSchema = v.pipe(
  v.object({
    id: v.string(),
    type: v.union([v.literal('songs'), v.literal('library-songs')]),
    attributes: v.pipe(
      v.object({
        artwork: v.optional(ArtWorkAttrSchema),
        artistName: v.string(),
        albumName: v.string(),
        discNumber: v.optional(v.number()),
        name: v.string(),
        playParams: v.object({
          id: v.string(),
          kind: v.literal('song'),
          isLibrary: v.optional(v.boolean()),
        }),
        releaseDate: v.optional(v.string()),
        trackNumber: v.optional(v.number()),
        contentRating: v.optional(v.string()),
      }),
      v.transform(
        (
          {
            artwork,
            artistName,
            albumName,
            discNumber,
            name,
            playParams,
            trackNumber,
            contentRating,
          },
        ) => ({
          artwork,
          artistName,
          albumName,
          discNumber,
          isLibrary: playParams.isLibrary ?? false,
          name,
          trackNumber,
          contentRating,
        }),
      ),
    ),
    relationships: v.optional(
      v.object({
        artists: v.optional(
          v.object({
            data: v.array(ArtistItemSchema),
          }),
        ),
        albums: v.optional(
          v.object({
            data: v.array(AlbumItemSchema),
          }),
        ),
      }),
    ),
  }),
  v.transform(({ id, attributes, relationships }) => {
    const {
      artwork,
      artistName,
      albumName,
      discNumber,
      name,
      isLibrary,
      trackNumber,
      contentRating,
    } = attributes;

    return {
      id,
      artwork,
      artistName,
      albumName,
      discNumber,
      isLibrary,
      name,
      trackNumber,
      contentRating,
      type: 'track' as const,
      ...(relationships
        ? ((aRelationships) => {
          const { artists, albums } = aRelationships;
          return {
            relationships: {
              artist: artists ? artists?.data[0] : undefined,
              album: albums ? albums?.data[0] : undefined,
            },
          };
        })(relationships)
        : {}),
    };
  }),
);

export type SongItem = v.InferOutput<typeof SongItemSchema>;

export type TrackItem = AlbumTrackItem | PlaylistTrackItem | SongItem;

// -------------------------
// Recommendations
// -------------------------

export const DEFAULT_MAX_RECOMMEND_CONTENTS = Number(
  import.meta.env.VITE_DEFAULT_MAX_RECOMMEND_CONTENTS ?? 6,
);

export const RecommendsBaseSchema = v.object({
  id: v.string(),
  type: v.literal('personal-recommendation'),
  attributes: v.pipe(
    v.object({
      resourceTypes: v.array(
        v.union([
          v.literal('albums'),
          v.literal('playlists'),
          v.literal('stations'),
        ]),
      ),
      title: v.object({
        stringForDisplay: v.string(),
      }),
      kind: v.union([
        v.literal('recently-played'),
        v.literal('music-recommendations'),
      ]),
    }),
    v.transform(({ title, kind }) => ({
      title: title ? title.stringForDisplay : '',
      kind,
    })),
  ),
});

export const RecommendListSchema = v.pipe(
  v.object({
    ...RecommendsBaseSchema.entries,
    relationships: v.object({
      contents: v.object({
        data: v.array(
          v.union([
            AlbumItemSchema,
            PlaylistItemSchema,
            StationItemSchema,
          ]),
        ),
      }),
    }),
  }),
  v.transform(({ id, attributes, relationships }) => {
    const { title, kind } = attributes;
    return {
      id,
      kind,
      title,
      resources: relationships.contents.data,
      hasMore:
        relationships.contents.data.length > DEFAULT_MAX_RECOMMEND_CONTENTS,
    };
  }),
);

/**
 * Type for the original response of the `/v1/me/recommendations` endpoint.
 */
export type RecommendListInput = v.InferInput<
  typeof RecommendListSchema
>;

/**
 * Type for the recommendation list.
 */
export type RecommendList = v.InferOutput<
  typeof RecommendListSchema
>;

/**
 * Schema for the recommendation group.
 * This is used for the `/v1/me/recommendations` endpoint.
 */
export const RecommendGroupSchema = v.pipe(
  RecommendsBaseSchema,
  v.transform(({ id, attributes }) => {
    const { title, kind } = attributes;
    return {
      id,
      kind,
      title,
    };
  }),
);

/**
 * Type for the recommendation group.
 */
export type RecommendGroup = v.InferOutput<
  typeof RecommendGroupSchema
>;
